import React, { useEffect, useRef, useState } from "react";
import { Upload, Images, X, Loader2, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Progress } from "../ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { errorMessage, resolveMedia, uploadImage, uploadsApi } from "../../lib/api";
import { cn } from "../../lib/utils";

const inputCls = "h-10 rounded-none border-border bg-transparent px-3 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";
const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/avif";
const MAX_MB = 15;

const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

/** Returns an error message for an unusable file, or null when it can be uploaded. */
export const validateImageFile = (file) => {
  if (!file) return "No file selected";
  if (!ACCEPT.split(",").includes(file.type)) return "Please choose a JPG, PNG, WEBP, GIF or AVIF image";
  if (file.size > MAX_MB * 1024 * 1024) return `Image must be under ${MAX_MB} MB`;
  return null;
};

/** Media library popover: pick from previously uploaded images. */
const Library = ({ onPick, currentUrl }) => {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setItems(null);
    uploadsApi
      .list()
      .then(setItems)
      .catch((err) => {
        toast.error(errorMessage(err, "Could not load library"));
        setItems([]);
      });
  }, [open]);

  const remove = async (e, item) => {
    e.stopPropagation();
    try {
      await uploadsApi.remove(item.id);
      setItems((list) => list.filter((x) => x.id !== item.id));
      toast.success("Removed from library");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-10 rounded-none" data-testid="image-library-trigger">
          <Images className="mr-2 h-4 w-4" /> Library
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[420px] max-w-[92vw] rounded-none border-border bg-card p-3" data-testid="image-library">
        <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Uploaded images</p>
        {items === null ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No uploads yet. Use the Upload button to add your first image.</p>
        ) : (
          <div className="grid max-h-72 grid-cols-3 gap-2 overflow-y-auto pr-1">
            {items.map((item) => {
              const selected = currentUrl === item.url;
              return (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => {
                    onPick(item.url);
                    setOpen(false);
                  }}
                  className={cn("group relative aspect-[4/3] overflow-hidden border bg-background text-left", selected ? "border-primary" : "border-border hover:border-foreground/50")}
                  title={item.original_name}
                  data-testid={`library-item-${item.id}`}
                >
                  <img src={resolveMedia(item.url)} alt={item.original_name} className="h-full w-full object-cover" loading="lazy" />
                  {selected && (
                    <span className="absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center bg-primary text-primary-foreground">
                      <Check className="h-3 w-3" />
                    </span>
                  )}
                  <span
                    role="button"
                    tabIndex={-1}
                    onClick={(e) => remove(e, item)}
                    className="absolute right-1 top-1 hidden h-6 w-6 items-center justify-center bg-background/80 text-foreground hover:text-destructive group-hover:flex"
                    aria-label="Remove from library"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </span>
                  <span className="absolute inset-x-0 bottom-0 truncate bg-background/80 px-1.5 py-0.5 text-[10px] text-muted-foreground">{fmtSize(item.size)}</span>
                </button>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

/**
 * Image URL input with chunked upload + media library + preview.
 * value/onChange operate on the stored URL string.
 */
const ImageField = ({ label = "Image", value, onChange, hint, placeholder = "https://… or upload", testId = "image-field" }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null); // null | 0..100
  const [fileName, setFileName] = useState("");

  const pick = () => inputRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    const problem = validateImageFile(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    setFileName(file.name);
    setProgress(0);
    try {
      const rec = await uploadImage(file, setProgress);
      onChange(rec.url);
      toast.success("Image uploaded", { description: file.name });
    } catch (err) {
      toast.error(errorMessage(err, "Upload failed"));
    } finally {
      setTimeout(() => setProgress(null), 600);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer?.files?.[0]);
  };

  const uploading = progress !== null;

  return (
    <div className="space-y-2" data-testid={testId}>
      <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
      <div className="flex gap-2">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cn(inputCls, "flex-1")} data-testid={`${testId}-url`} />
        <Button type="button" variant="outline" size="sm" className="h-10 rounded-none" onClick={pick} disabled={uploading} data-testid={`${testId}-upload`}>
          {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          Upload
        </Button>
        <Library onPick={onChange} currentUrl={value} />
        <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid={`${testId}-file`} />
      </div>

      {uploading && (
        <div className="space-y-1.5 border border-border bg-background p-3" data-testid={`${testId}-progress`}>
          <div className="flex items-center justify-between text-xs">
            <span className="truncate text-muted-foreground">{progress < 100 ? `Uploading ${fileName}…` : "Finishing up…"}</span>
            <span className="tabular-nums">{progress}%</span>
          </div>
          <Progress value={progress} className="h-1.5 rounded-none bg-secondary [&>div]:bg-primary" />
        </div>
      )}

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className={cn("relative overflow-hidden border border-dashed border-border bg-background", value ? "aspect-[16/7]" : "flex h-20 items-center justify-center")}
        data-testid={`${testId}-preview`}
      >
        {value ? (
          <>
            <img src={resolveMedia(value)} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center bg-background/80 text-foreground hover:text-destructive"
              aria-label="Clear image"
              data-testid={`${testId}-clear`}
            >
              <X className="h-4 w-4" />
            </button>
          </>
        ) : (
          <button type="button" onClick={pick} className="text-xs text-muted-foreground hover:text-foreground">
            Drop an image here or click to upload
          </button>
        )}
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
};

export default ImageField;
